<template>
    <el-upload
        ref="ossElUpload"
        :class="['upload-demo', customClass, { disabled }]"
        action="upload"
        :accept="accept"
        :drag="drag"
        :multiple="multiple"
        :http-request="submitUpload"
        :show-file-list="showFileList"
        :disabled="disabled"
        :before-upload="handleBeforeUpload"
        :on-remove="handleRemove"
    >
        <el-button
            type="primary"
            :disabled="disabled"
            :loading="loading"
            v-if="type === 'button'"
        >{{ text }}</el-button>
        <el-button
            type="text"
            :disabled="disabled"
            :loading="loading"
            v-if="type === 'text'"
        >{{ text }}</el-button>
        <span v-if="type === 'icon'" :class="['customUploadIcon', { disabled }]">
            <i :class="icon" ref="icon"></i>
        </span>
    </el-upload>
</template>
<script>
import { ossUpload } from '@/components/lots/OSS/api.js';
import { imageMinimize } from './imageMinimize.js';
const BYTES_UNIT = 1024;
export default {
    name: 'PerformanceCommonUpload',
    props: {
        bucket: {
            type: String,
            default: 'c-tms'
        },
        businessKey: {
            type: String,
            default: 'default'
        },
        publicRead: {
            type: [Number, String],
            default: 1
        },
        contentType: { // 上传内容类型
            type: String,
            default: 'application/octet-stream'
        },
        showFileList: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        drag: {
            type: Boolean,
            default: false
        },
        type: {
            type: String,
            default: 'button'
        },
        text: {
            type: String,
            default: '请选择文件'
        },
        icon: {
            type: String,
            default: 'el-icon-upload2'
        },
        accept: {
            type: String,
            default: '.png,.jpg,.jpeg,.dox,.docx,.xls,.xlsx'
        },
        maxSize: { // 单张文件大小，默认8M
            type: Number,
            default: 8
        },
        multiple: { // 是否支持多选上传
            type: Boolean,
            default: false
        },
        onlyUploadOne: { // 仅支持上传单张，若已上传了文件，则替换上一次上传的文件
            type: Boolean,
            default: false
        },
        compressImage: { // 5MB以上图片上传，是否进行图片压缩后再上传
            type: Boolean,
            default: false
        },
        compressQuality: { // 图片压缩的质量
            type: Number,
            default: 0.5
        },
        customClass: {
            type: String,
            default: ''
        }
    },
    data () {
        return {
            uploadList: [], // 上传文件响应集合
            uploadFileList: [], // 文件上传前的临时文件
            loading: false, // 上传按钮加载动画
            cancelUpload: false // 是否取消上传
        };
    },
    methods: {
        /**
         * @description 文件上传之前回调
         * @param {Object} file 文件对象
         */
        handleBeforeUpload (file) {
            // 限制文件上传大小
            if (file.size > this.maxSize * BYTES_UNIT * BYTES_UNIT && !this.compressImage) {
                this.deleteUploadFiles(file);
                this.cancelUpload = true;
                return this.$message.warning(`上传文件大于${this.maxSize}MB，请重新上传`);
            }
            this.cancelUpload = false;
            // 是否只允许上传一张
            if (this.onlyUploadOne && this.$refs.ossElUpload.uploadFiles.length > 1) {
                const uploadFiles = this.$refs.ossElUpload.uploadFiles;
                uploadFiles.splice(0, uploadFiles.length, file);
            }
            // 多选时，将上传前的文件保存起来
            if (this.multiple) {
                this.uploadFileList.push(file);
            }
        },
        /**
         * @description 删除已上传文件
         * @param {Object} file 文件对象
         */
        deleteUploadFiles (file) {
            const uploadFiles = this.$refs.ossElUpload.uploadFiles;
            const delFileIndex = uploadFiles.findIndex(v => (file.uid === v.uid));
            uploadFiles.splice(delFileIndex, 1);
        },
        // 移除文件回调
        handleRemove (file, fileList) {
            const delFileIndex = this.uploadList.findIndex(v => (v.uid === file.uid));
            this.uploadList.splice(delFileIndex, 1);
            const delIndex = this.uploadFileList.findIndex(v => (v.uid === file.uid));
            this.uploadFileList.splice(delIndex, 1);
            this.$emit('remove', this.uploadList);
        },
        async imageFileProcess (file) {
            const extensionTest = file.name.substring(file.name.lastIndexOf('.') + 1);
            if (extensionTest !== 'jpeg' && extensionTest !== 'jpg' && extensionTest !== 'png' && extensionTest !== 'gif') {
                return file;
            }
            const minimize = this.compressImage ? await imageMinimize(file, this.compressQuality, this.maxSize) : file;
            return minimize;
        },
        /**
         * @description 上传
         * @param {Object} uploadData 上传后的数据
         */
        async submitUpload (uploadData) {
            if (this.cancelUpload) { return; }
            this.$emit('beforeUpload', uploadData.file);
            const theFile = await this.imageFileProcess(uploadData.file);
            const file = new File([theFile], uploadData.file.name, {
                type: uploadData.file.type
            });
            this.loading = true;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('bucket', this.bucket);
            formData.append('businessKey', this.businessKey);
            formData.append('publicRead', this.publicRead);
            formData.append('contentType', this.contentType);
            ossUpload(formData).then((res) => {
                if (res && +res.code === 0) {
                    if (this.onlyUploadOne) {
                        this.uploadList.splice(0, this.uploadList.length, {
                            ...this.callbackFileInfo(res.data.downUrl, uploadData.file),
                            uid: uploadData.file.uid
                        });
                    } else {
                        this.uploadList.push({
                            ...this.callbackFileInfo(res.data.downUrl, uploadData.file),
                            uid: uploadData.file.uid
                        });
                    }
                    if (this.multiple) { // 批量上传
                        // 如果响应的文件个数等于上传前的文件个数，才回调callback事件（即保证多张图片全部上传成功后再回调）
                        if (this.uploadList.length === this.uploadFileList.length) {
                            this.$emit('callback', this.uploadList);
                        }
                    } else {
                        // 单张上传
                        this.$emit('callback', {
                            ...this.callbackFileInfo(res.data.downUrl, uploadData.file),
                            uid: uploadData.file.uid
                        });
                    }
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        /**
         * @description 处理上传成功回调参数
         * @param {String} url oss响应的url
         * @param {Object} file 文件对象
         */
        callbackFileInfo (url, file) {
            return {
                fileUrl: url,
                fileSize: file.size,
                fileName: file.name
            };
        }
    }
};
</script>
<style lang="less">
.customUploadIcon [class^='el-icon-'] {
    color: #00bcbf;
}
.upload-demo.disabled .customUploadIcon [class^='el-icon-'] {
    cursor: default;
    color: #aaa;
    border-color: #aaa;
}
.upload-demo {
    .el-upload {
        text-align: left;

        .el-upload-dragger {
            width: auto;
            height: auto;
            line-height: 1;
            overflow: auto;
            position: static;
        }
    }
}
</style>
